import type { BatchRequest } from '../types/compute';
import type { ReconciliationDraft } from './batchRequestBuilder';

/**
 * The identity of one configured chart within a reconciliation.
 *
 * It is an opaque string, MINTED once and carried by the algorithm itself. It
 * used to be *derived* from position — `(name, occurrence)` counted over
 * `draft.algorithms` — and that is the bug this module exists to remove:
 * deleting one instance renamed every later instance of the same algorithm,
 * while the executed result kept the old names. The survivor's chart then
 * pointed at an id the draft no longer had, so its edit / duplicate / delete
 * became silent no-ops until the next run.
 *
 * Position is ORDER; it is not identity. The two are separate here:
 *
 *   identity  → `instanceId`, minted, immune to deletion and reordering
 *   order     → the array; what the user reads comes from `displayOrdinal`
 *   figure    → a chart id, an artifact of one run (see resultToDashboard)
 *
 * Treat the string as opaque. Never parse it for the algorithm name or an
 * ordinal — that is what made it fragile. Uniqueness is only ever needed WITHIN
 * one reconciliation: ids live in that reconciliation's config, its snapshots
 * are its own rows, and opening another one resets the selection, so two
 * reconciliations never share a container.
 *
 * THE RULE for callers, because the two cases pull in opposite directions:
 *
 *   RESOLVING a reference (`instanceIndex`, `displayOrdinal`) is STRICT. The id
 *     comes from somewhere that may be out of date — an executed chart, a stored
 *     selection — so a positional fallback could act on whatever now occupies
 *     that slot. Not resolving is the safe answer; the caller no-ops.
 *   ENUMERATING a list to render it (`ensureInstanceIds` then map) is TOLERANT.
 *     There is no stale reference in play: each row just needs an id to be keyed
 *     by. For any draft the app builds this is a no-op, since the store mints on
 *     add/duplicate and `batchRequestToDraft` materialises on load.
 *
 * `instanceId` is optional on `AlgorithmInput` only so the many fixtures that
 * build an algorithm literal need not invent one. Making it required would let
 * the compiler enforce the invariant and retire the tolerant half of this rule —
 * worth doing, and deliberately not bundled into this change.
 */

/** The minimal shape every helper here needs. */
interface InstanceLike {
  name: string;
  instanceId?: string;
  /** See `AlgorithmEntry.figure_title` (types/compute.ts) / SPEC/2240. */
  figureTitle?: string;
}

/** Same list, with every entry's id guaranteed. */
type Identified<T> = T & { instanceId: string };

/**
 * The id a config with no `instance_id` implied: its position when it was saved.
 *
 * Materialising exactly this string — rather than minting something new — is
 * what keeps every stored reference valid for reconciliations saved before ids
 * existed: their snapshots, report blocks and selections already spell
 * `name#occurrence`. From the moment it is written down it stops moving.
 */
export function positionalInstanceId(algorithm: string, occurrence: number): string {
  return `${algorithm}#${occurrence}`;
}

/**
 * 122 random bits (a v4 UUID pins 6 of its 128), or 128 from the fallback —
 * either way enough that uniqueness holds on its own rather than resting on the
 * caller having assembled a complete `taken`; see {@link mintInstanceId} for why
 * it cannot be complete. Long in the stored JSON is the price; the id is opaque,
 * so nothing but a human reading raw config pays it. What it replaces is
 * `Math.random` in base-36, whose 31 bits left `taken` as the only guarantee.
 *
 * Why a fallback at all: `crypto.randomUUID` is secure-context only and the dev
 * server listens on every interface (`vite.config.ts`, `host: true`), so reached
 * by LAN IP over http it is undefined. `getRandomValues` carries no such gate.
 *
 * And why a throw rather than a third fallback: a weak id here is not a
 * degradation, it is two charts answering to one identity — silently, in
 * persisted notes and colours. Refusing with a cause the reader can act on beats
 * inventing one. Both callers catch it and route the message to the store's `error`
 * banner (addAlgorithm, duplicateAlgorithm) — which they must, because they run in
 * click handlers: React sends a handler's throw to neither an error boundary nor
 * the UI, and this app has no error boundary at all, so an uncaught one would show
 * the user nothing but a card that never appears.
 *
 * Exported because report block ids need exactly this and must not grow a second
 * randomness source: two of them would drift on the secure-context fallback, and
 * the one that drifted would be the one nobody reads until a client PDF is wrong.
 */
export function randomSuffix(): string {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === 'function') return source.randomUUID();
  if (typeof source?.getRandomValues === 'function') {
    const bytes = source.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  throw new Error(
    'No hay fuente de aleatoriedad: este navegador no expone crypto.randomUUID ni crypto.getRandomValues. Abre la app por https o por localhost.'
  );
}

/**
 * A fresh identity for a new chart, guaranteed distinct from `taken`.
 *
 * `taken` should carry the ids of the executed config as well as the draft's (see
 * {@link takenInstanceIds}) — reusing a deleted instance's id would hand its
 * still-plotted figures to the newcomer, the same confusion one level deeper. But
 * it CANNOT be complete: it is built from the draft and the last-SAVED config,
 * while the row also keeps an `execution_config` the frontend never reads. So
 * `taken` is a safety net and the suffix's entropy is the actual guarantee.
 * The name is a prefix purely so the stored JSON reads well; nothing parses it.
 */
export function mintInstanceId(algorithm: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  // Bounded on purpose. With 122 bits the first draw collides essentially never,
  // so more than a handful of retries does not mean bad luck — it means the
  // source is degenerate (a stubbed `crypto` in a test, a polyfill returning a
  // constant). Spinning forever inside a store action freezes the tab with no
  // message; failing says which of the two it was.
  for (let attempt = 0; attempt < 8; attempt++) {
    const id = `${algorithm}#${randomSuffix()}`;
    if (!used.has(id)) return id;
  }
  throw new Error(
    `No se pudo acuñar una identidad para "${algorithm}": la fuente de aleatoriedad devuelve valores repetidos.`
  );
}

/**
 * A fresh figure name for a new or duplicated instance, unique against `taken`
 * (every figureTitle already used in the reconciliation — see SPEC/2240).
 *
 * `baseLabel` is a human label (the algorithm's display name for a fresh
 * instance, or the original's own name for a duplicate) — unlike
 * `mintInstanceId`, this is read by the user, so collisions are resolved with
 * a readable `#2`, `#3`, … suffix rather than a random one, and the bare label
 * wins outright when nothing else has claimed it (the first histogram is just
 * "Histograma", not "Histograma #1").
 */
export function mintFigureTitle(baseLabel: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(baseLabel)) return baseLabel;
  let n = 2;
  while (used.has(`${baseLabel} #${n}`)) n += 1;
  return `${baseLabel} #${n}`;
}

/**
 * Whether `title` is already the figureTitle of some OTHER instance —
 * figureTitle must be unique within one reconciliation (SPEC/2240), same scope
 * as instanceId. Blank titles never collide: an instance without one yet
 * (mid-materialisation, or a fixture) doesn't block a real name from being set.
 */
export function isFigureTitleTaken(
  algorithms: readonly InstanceLike[],
  title: string,
  excludeInstanceId?: string | null
): boolean {
  const t = title.trim();
  if (!t) return false;
  return algorithms.some((a) => a.instanceId !== excludeInstanceId && a.figureTitle?.trim() === t);
}

/**
 * Give every entry an id, keeping the ones already present.
 *
 * This is the ONLY place a positional fallback is allowed, and it happens once —
 * when a config that predates minting is read. Afterwards the id is written
 * down and stops depending on where the entry sits.
 *
 * The occurrence counter advances over EVERY entry of a name, whether or not it
 * already carries an id, so the id handed to the second `scatter` is the one
 * that scheme implied for the second `scatter` — never the first's. Present ids
 * are also skipped explicitly, which covers a half-migrated config where an
 * entry already holds the string another would have been given.
 */
export function ensureInstanceIds<T extends InstanceLike>(list: readonly T[]): Identified<T>[] {
  return materialise(list, { dedupe: false });
}

/**
 * Draft-side materialisation: like {@link ensureInstanceIds}, but every entry
 * ends up with a DISTINCT id.
 *
 * A stored config that used the tonnage by-category fan-out holds several
 * entries sharing one chart's id (the fan-out is lowered on save and never
 * re-collapsed on load), so reading it back yields that many separate
 * configured charts. They cannot all answer to the same identity: the first
 * keeps it, so references already pointing there still resolve, and the rest are
 * given their own.
 */
export function uniqueInstanceIds<T extends InstanceLike>(list: readonly T[]): Identified<T>[] {
  return materialise(list, { dedupe: true });
}

/**
 * Whether an entry already carries a usable identity.
 *
 * A type predicate rather than a boolean because the rule and the assertion have to
 * be the SAME code. Written as `const hasId = ...` TypeScript does not narrow
 * `entry.instanceId` — it is a property of a generic `T` — so the caller needed two
 * `as string`, and relaxing the guard would have left those casts asserting
 * something no longer checked.
 *
 * `''` and `null` are rejected on purpose: `Identified<T>` promises a string, and an
 * empty id becomes the chart id `-error-lp`, which chartTree and reportBlocks read
 * as "not attributable" — the tree calls the instance undrawn while its card is on
 * screen, and the figure is never offered to the report. Nothing in the FRONTEND
 * writes a falsy id (batchRequestBuilder only stamps truthy), but a config arrives
 * as raw JSON the backend never inspects (`Config json.RawMessage`), so
 * `instance_id` can reach here as `42` or `null` from a hand-edited row. This is
 * that boundary's only runtime check — not a latch on a door that is already shut.
 */
function hasInstanceId<T extends InstanceLike>(entry: T): entry is Identified<T> {
  return typeof entry.instanceId === 'string' && entry.instanceId !== '';
}

function materialise<T extends InstanceLike>(
  list: readonly T[],
  { dedupe }: { dedupe: boolean }
): Identified<T>[] {
  const present = new Set(
    list.map((entry) => entry.instanceId).filter((id): id is string => id !== undefined)
  );
  const walked = new Map<string, number>();
  const claimed = new Set<string>();

  return list.map((entry) => {
    const occurrence = walked.get(entry.name) ?? 0;
    walked.set(entry.name, occurrence + 1);

    // See hasInstanceId: `!== undefined` used to let `''` and `null` through.
    if (hasInstanceId(entry) && !(dedupe && claimed.has(entry.instanceId))) {
      claimed.add(entry.instanceId);
      return entry;
    }

    let n = occurrence;
    let id = positionalInstanceId(entry.name, n);
    while (present.has(id) || claimed.has(id)) {
      n += 1;
      id = positionalInstanceId(entry.name, n);
    }
    present.add(id);
    claimed.add(id);
    return { ...entry, instanceId: id };
  });
}

/**
 * Index of the entry carrying `instanceId`, or -1.
 *
 * Deliberately strict: an entry with no id does NOT match by position. A
 * positional fallback here would resolve a stale reference to whatever now
 * occupies that slot — silently acting on the wrong chart, which is worse than
 * doing nothing. Callers hold drafts that went through {@link ensureInstanceIds},
 * so a -1 means the instance is genuinely gone.
 */
export function instanceIndex(
  algorithms: readonly InstanceLike[],
  instanceId: string | null
): number {
  if (!instanceId) return -1;
  return algorithms.findIndex((a) => a.instanceId === instanceId);
}

/**
 * The 1-based number the user reads next to a chart's name ("Histograma #2"):
 * its position among the instances of that algorithm CURRENTLY configured.
 *
 * Unlike the identity this does renumber when a sibling is deleted, and it
 * should — a label reading "#3" when only two exist is simply wrong. 0 when the
 * id resolves to nothing.
 *
 * `keyOf` decides what counts as "that algorithm". It defaults to the raw name,
 * and every caller that shows a chart's number passes `pickerSlugFor` instead,
 * because two Lib names can be one chart to the user: the tonnage algorithm
 * switches name when "Agrupar por categoría" is on, and counting by raw name
 * numbered both modes "#1". It is injected rather than imported so this module
 * stays free of the catalog (which carries React icon values).
 */
export function displayOrdinal(
  algorithms: readonly InstanceLike[],
  instanceId: string,
  keyOf: (name: string) => string = (name) => name
): number {
  const i = instanceIndex(algorithms, instanceId);
  if (i < 0) return 0;
  const key = keyOf(algorithms[i].name);
  return algorithms.slice(0, i + 1).filter((a) => keyOf(a.name) === key).length;
}

/** Each draft algorithm's id, in draft order (materialising any that are absent). */
export function draftInstanceIds(draft: ReconciliationDraft): string[] {
  return ensureInstanceIds(draft.algorithms).map((a) => a.instanceId);
}

/**
 * The instance id of each entry of an executed config, aligned to
 * `config.algorithms` by index and materialised for configs that predate
 * `instance_id`.
 *
 * Fan-out entries repeat their parent chart's id — that repetition is exactly
 * what lets several result envelopes resolve back to one configured chart.
 */
export function executedInstanceIds(config?: BatchRequest): string[] {
  const entries = config?.algorithms ?? [];
  return ensureInstanceIds(
    entries.map((entry) => ({ name: entry.name, instanceId: entry.instance_id }))
  ).map((entry) => entry.instanceId);
}

/**
 * Every id a live reference could still point at: the draft's plus the executed
 * config's. What {@link mintInstanceId} must avoid — the executed side matters
 * because its charts are still on screen after their instance is deleted.
 */
export function takenInstanceIds(draft: ReconciliationDraft, config?: BatchRequest): Set<string> {
  const ids = new Set(draftInstanceIds(draft));
  for (const id of executedInstanceIds(config)) ids.add(id);
  return ids;
}
